import { Link } from "react-router-dom";
import {
  ArrowRight,
  Layers,
  Shield,
  BarChart3,
  Users,
  Zap,
  Globe,
  ChevronDown,
  Clock,
  Puzzle,
  TrendingUp,
  Database,
  Send,
  ClipboardCheck,
  LineChart,
  Quote,
  Building2,
} from "lucide-react";
import heroBg from "@/assets/hero-bg.jpg";
import cadenaLogo from "@/assets/cadena-zero-logo.webp";

/* ─── DATA ─── */

const features = [
  { icon: Users, title: "Supplier Engagement", desc: "Onboard y gestiona proveedores en una red privada y segura." },
  { icon: Shield, title: "Compliance Tools", desc: "Despliega encuestas, checklists y evaluaciones con plantillas listas." },
  { icon: BarChart3, title: "Performance Scoring", desc: "KPIs y scoring personalizados por industria y objetivos." },
  { icon: Zap, title: "Improvement Plans", desc: "Planes de mejora accionables, trazables y con evidencia." },
  { icon: Globe, title: "Impact Management", desc: "Mide y reporta impacto ESG en toda tu cadena de valor." },
  { icon: Layers, title: "Modular Architecture", desc: "Construye solo lo que necesitas, sin bloat ni complejidad." },
];

const steps = [
  { num: "01", icon: Send, title: "Conecta", desc: "Invita proveedores a tu red privada y organízalos por categorías o segmentos." },
  { num: "02", icon: ClipboardCheck, title: "Evalúa", desc: "Crea encuestas y assessments, y despliégalos a los segmentos que elijas." },
  { num: "03", icon: LineChart, title: "Mejora", desc: "Revisa resultados, puntuaciones y activa planes de mejora concretos." },
];

const benefits = [
  { icon: Clock, title: "Ahorra tiempo", desc: "Todo en un solo lugar, sin hojas de cálculo ni correos interminables." },
  { icon: Puzzle, title: "Reduce complejidad", desc: "Segmentos, plantillas y flujos claros que simplifican tu operación." },
  { icon: TrendingUp, title: "Aumenta eficiencia", desc: "Respuestas automáticas y scoring calculado en tiempo real." },
  { icon: Database, title: "Mejores decisiones", desc: "Datos centralizados para reporting, ESG y gobierno de cadena." },
];

const metrics = [
  { value: "500+", label: "Proveedores gestionados" },
  { value: "40%", label: "Menos tiempo en evaluaciones" },
  { value: "50+", label: "Organizaciones activas" },
];

const testimonials = [
  {
    quote: "Cadena Zero nos permitió tener visibilidad completa de nuestra base de proveedores en semanas, no meses.",
    name: "María López",
    role: "Procurement Manager",
  },
  {
    quote: "La modularidad es clave. Creamos las herramientas exactas que necesitábamos sin comprar un ERP completo.",
    name: "Carlos Ruiz",
    role: "Sustainability Director",
  },
];

/* ─── HELPERS ─── */

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

/* ─── COMPONENT ─── */

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* ══════ NAV ══════ */}
      <nav className="fixed top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-2.5">
            <img src={cadenaLogo} alt="Cadena Zero" width={32} height={32} className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-display text-lg font-bold text-foreground">Cadena Zero</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/auth"
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
            >
              Acceder
            </Link>
          </div>
        </div>
      </nav>

      {/* ══════ HERO ══════ */}
      <section className="relative flex min-h-[90vh] items-center overflow-hidden pt-16">
        <img
          src={heroBg}
          alt=""
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/50" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-20 md:py-32">
          <div className="max-w-2xl animate-fade-in">
            <span className="mb-4 inline-flex items-center rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
              Value Chain Impact Platform
            </span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight tracking-tight text-primary-foreground sm:text-5xl md:text-6xl">
              Gestiona tu cadena de suministro{" "}
              <span className="text-gradient-brand">en un solo lugar.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-primary-foreground/80">
              Conecta con proveedores, mide su desempeño y mejora la sostenibilidad de tu cadena — todo desde una plataforma privada y modular.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/auth"
                className="inline-flex items-center gap-2 rounded-xl bg-accent px-7 py-3.5 text-sm font-semibold text-accent-foreground shadow-lg transition-all hover:bg-accent/90 hover:shadow-xl"
              >
                Comenzar <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="inline-flex items-center gap-2 rounded-xl border border-primary-foreground/25 bg-primary-foreground/10 px-6 py-3.5 text-sm font-semibold text-primary-foreground backdrop-blur-sm transition-all hover:bg-primary-foreground/20"
              >
                Ver cómo funciona <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ VALUE PROPOSITION ══════ */}
      <section className="border-b border-border bg-card py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10 md:grid-cols-3 md:gap-12">
            {[
              {
                title: "Qué hace",
                text: "Una plataforma privada donde tu empresa y sus proveedores colaboran, completan evaluaciones y miden impacto.",
              },
              {
                title: "Para quién",
                text: "Equipos de procurement, sostenibilidad y supply chain que gestionan múltiples proveedores.",
              },
              {
                title: "Por qué es diferente",
                text: "Herramientas modulares, sin bloat. Crea encuestas, segmenta proveedores y obtén resultados en minutos.",
              },
            ].map((item, i) => (
              <div key={i} className="text-center md:text-left">
                <h2 className="font-display text-sm font-bold uppercase tracking-wider text-accent">{item.title}</h2>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ FEATURES ══════ */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-14 text-center">
            <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl">
              Todo lo que necesitas para gobernar tu cadena
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
              Herramientas modulares diseñadas para equipos de procurement y sostenibilidad.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <div
                key={i}
                className="group rounded-xl border border-border bg-card p-6 shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-semibold text-card-foreground">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ HOW IT WORKS ══════ */}
      <section id="how-it-works" className="border-y border-border bg-secondary/40 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-14 text-center">
            <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl">
              Cómo funciona
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
              Tres pasos para transformar la gestión de tu cadena de suministro.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3 md:gap-6">
            {steps.map((s, i) => (
              <div key={i} className="relative rounded-xl border border-border bg-card p-8 text-center shadow-card">
                <span aria-hidden="true" className="font-display text-4xl font-extrabold text-accent/30">{s.num}</span>
                <div className="mx-auto mt-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent">
                  <s.icon className="h-7 w-7" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-card-foreground">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ BENEFITS ══════ */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-14 text-center">
            <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl">
              Beneficios que importan
            </h2>
          </div>
          <div className="grid gap-6 grid-cols-2 lg:grid-cols-4">
            {benefits.map((b, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-6 text-center shadow-card">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent">
                  <b.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-base font-bold text-card-foreground">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ SOCIAL PROOF ══════ */}
      <section className="border-y border-border bg-secondary/40 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          {/* Metrics */}
          <div className="mb-16 grid gap-6 grid-cols-3">
            {metrics.map((m, i) => (
              <div key={i} className="text-center">
                <p className="font-display text-3xl font-extrabold text-accent md:text-4xl">{m.value}</p>
                <p className="mt-1 text-sm text-muted-foreground">{m.label}</p>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid gap-8 md:grid-cols-2">
            {testimonials.map((t, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-8 shadow-card">
                <Quote className="mb-4 h-8 w-8 text-accent/30" />
                <p className="text-base italic leading-relaxed text-card-foreground">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-card-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Logo placeholders */}
          <div className="mt-16 text-center">
            <p className="mb-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Empresas que confían en Cadena Zero
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex h-10 w-28 items-center justify-center rounded-md border border-border bg-muted/50 text-xs font-medium text-muted-foreground"
                >
                  Logo
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════ FINAL CTA ══════ */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/80" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-primary-foreground md:text-4xl">
            ¿Listo para transformar tu cadena de suministro?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-primary-foreground/75">
            Únete a las organizaciones que ya gestionan proveedores, evaluaciones e impacto desde un solo lugar.
          </p>
          <Link
            to="/auth"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-accent px-8 py-4 text-base font-semibold text-accent-foreground shadow-lg transition-all hover:bg-accent/90 hover:shadow-xl"
          >
            Comenzar ahora <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* ══════ FOOTER ══════ */}
      <footer className="border-t border-border bg-card py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src={cadenaLogo} alt="Cadena Zero" width={20} height={20} className="h-5 w-5 rounded object-cover" />
            <span className="font-display text-sm font-bold text-foreground">Cadena Zero</span>
          </div>
          <p className="text-sm text-muted-foreground">© 2026 Cadena Zero. All rights reserved.</p>
          <Link to="/auth" className="text-sm font-medium text-accent hover:underline">
            Acceder →
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
